import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AppUrl from '../../restApi/AppUrl';
import IRestClient from '../../restApi/IRestClient';
import { FaPlay, FaVideo, FaTimes, FaArrowRight } from 'react-icons/fa';
import { FaYoutube } from 'react-icons/fa';
import SectionHeader from '../SectionHeader/SectionHeader';
import bgImg from '../../img/bg.jpeg';

class VideoGallery extends Component {
  constructor() {
    super();
    this.state = {
      myData: [],
      hoveredVideo: null,
      selectedVideo: null,
      isModalOpen: false,
      loading: true,
      thumbErrors: {}
    };
  }

  componentDidMount() {
    IRestClient.GetRequest(AppUrl.videos).then(result => {
      this.setState({ myData: result || [], loading: false });
    }).catch(() => {
      this.setState({ loading: false });
    });
  }

  componentWillUnmount() {
    document.body.style.overflow = '';
  }

  getYouTubeVideoId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];
    for (let pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  getYouTubeThumbnail = (url, quality = 'hqdefault') => {
    const id = this.getYouTubeVideoId(url);
    return id ? `https://img.youtube.com/vi/${id}/${quality}.jpg` : null;
  };

  getYouTubeEmbedUrl = (url) => {
    const id = this.getYouTubeVideoId(url);
    return id
      ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`
      : url;
  };

  handleThumbError = (index, url) => {
    const { thumbErrors } = this.state;
    if (!thumbErrors[index]) {
      // hqdefault failed — try mqdefault
      this.setState({ thumbErrors: { ...thumbErrors, [index]: 'mq' } });
    } else if (thumbErrors[index] === 'mq') {
      // Give up — show fallback
      this.setState({ thumbErrors: { ...thumbErrors, [index]: 'fallback' } });
    }
  };

  getThumbnailSrc = (url, index) => {
    const { thumbErrors } = this.state;
    if (thumbErrors[index] === 'fallback') return null;
    if (thumbErrors[index] === 'mq') return this.getYouTubeThumbnail(url, 'mqdefault');
    return this.getYouTubeThumbnail(url, 'hqdefault');
  };

  handleHover = (index) => this.setState({ hoveredVideo: index });
  handleLeave = () => this.setState({ hoveredVideo: null });

  openVideoModal = (videoData) => {
    this.setState({ selectedVideo: videoData, isModalOpen: true });
    document.body.style.overflow = 'hidden';
  };

  closeVideoModal = () => {
    this.setState({ selectedVideo: null, isModalOpen: false });
    document.body.style.overflow = 'unset';
  };

  renderCard = (vid, index, isFeatured = false) => {
    const { hoveredVideo, thumbErrors } = this.state;
    const isHovered = hoveredVideo === index;
    const thumbSrc = this.getThumbnailSrc(vid.video_link, index);
    const isFallback = thumbErrors[index] === 'fallback';

    return (
      <motion.div
        key={index}
        className="group relative rounded-2xl overflow-hidden cursor-pointer border border-white/10 bg-black"
        style={{
          boxShadow: isHovered
            ? '0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.12)'
            : '0 8px 32px rgba(0,0,0,0.5)'
        }}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.12 }}
        onMouseEnter={() => this.handleHover(index)}
        onMouseLeave={this.handleLeave}
        onClick={() => this.openVideoModal(vid)}
      >
        {/* Aspect wrapper — taller on mobile so title/button never crunch */}
        <div
          className="relative w-full overflow-hidden"
          style={{
            paddingBottom: isFeatured ? 'clamp(220px, 60vw, 400px)' : 'clamp(200px, 56.25vw, 360px)'
          }}
        >

          {/* Thumbnail */}
          {!isFallback && thumbSrc ? (
            <img
              src={thumbSrc}
              alt={vid.video_title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              onError={() => this.handleThumbError(index, vid.video_link)}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800">
              <FaVideo className="text-white/15 text-6xl" />
            </div>
          )}

          {/* Deep gradient — covers bottom 55% so text always readable */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.75) 30%, rgba(0,0,0,0.2) 60%, transparent 100%)'
            }}
          />
          <div
            className="absolute inset-0 bg-black/20 transition-opacity duration-300"
            style={{ opacity: isHovered ? 1 : 0 }}
          />

          {/* Top badges row */}
          <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
            {/* Impact badge */}
            <div className="flex items-center gap-1.5 bg-black/60 backdrop-blur-md border border-white/15 px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-white/80 text-[10px] sm:text-xs font-semibold uppercase tracking-wider">
                {isFeatured ? 'Featured' : 'Impact Story'}
              </span>
            </div>

            {/* YouTube logo */}
            <div className="flex items-center gap-1.5 bg-black/60 backdrop-blur-md border border-white/15 px-2.5 py-1 rounded-full">
              <FaYoutube className="text-red-500 text-sm" />
              <span className="text-white/70 text-[10px] font-semibold">YouTube</span>
            </div>
          </div>

          {/* Play button */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <motion.div
              className="relative flex items-center justify-center"
              animate={{ scale: isHovered ? 1.1 : 1 }}
              transition={{ duration: 0.3, type: 'spring', damping: 14 }}
            >
              {/* Ping ring */}
              {isHovered && (
                <span
                  className="absolute rounded-full border-2 border-white/30 animate-ping"
                  style={{
                    width: isFeatured ? '88px' : '72px',
                    height: isFeatured ? '88px' : '72px'
                  }}
                />
              )}
              {/* Button */}
              <div
                className="rounded-full bg-white/20 backdrop-blur-md border-2 border-white/40 flex items-center justify-center shadow-2xl transition-all duration-300 group-hover:bg-white/30 group-hover:border-white/60"
                style={{
                  width: isFeatured ? '72px' : '56px',
                  height: isFeatured ? '72px' : '56px',
                  boxShadow: isHovered ? '0 0 30px rgba(255,255,255,0.25)' : '0 4px 20px rgba(0,0,0,0.5)'
                }}
              >
                <FaPlay
                  className="text-white ml-0.5"
                  style={{ fontSize: isFeatured ? '20px' : '15px' }}
                />
              </div>
            </motion.div>
          </div>

          {/* Bottom info */}
          <div className="absolute bottom-0 left-0 right-0 px-4 pb-4 sm:px-5 sm:pb-5">
            <h3
              className={`text-white font-bold leading-tight mb-1.5 drop-shadow ${
                isFeatured
                  ? 'text-sm sm:text-xl md:text-2xl line-clamp-2'
                  : 'text-xs sm:text-sm line-clamp-1'
              }`}
            >
              {vid.video_title}
            </h3>
            <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs font-semibold">
              <FaPlay className="text-[8px]" />
              Watch Now
            </span>
          </div>
        </div>
      </motion.div>
    );
  };

  render() {
    const { myData, isModalOpen, selectedVideo, loading } = this.state;

    const featuredVideo = myData[0] || null;
    const secondaryVideos = myData.slice(1, 4);

    return (
      <>
        <section className="relative py-16 sm:py-20 overflow-hidden">

          {/* ── Background ── */}
          <div className="absolute inset-0">
            <div
              className="w-full h-full bg-cover bg-center bg-fixed"
              style={{ backgroundImage: `url(${bgImg})` }}
            />
            <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.74)' }} />
            <div
              className="absolute inset-0"
              style={{
                background:
                  'radial-gradient(ellipse 65% 55% at 10% 65%, rgba(46,139,84,0.18) 0%, transparent 65%), radial-gradient(ellipse 55% 45% at 90% 15%, rgba(27,43,92,0.22) 0%, transparent 65%)'
              }}
            />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* ── Header ── */}
            <SectionHeader
              badge="Video Gallery"
              title="Visual Stories of Hope"
              accentWord="Hope"
              subtitle="Watch how we're making a difference through these powerful impact stories."
              variant="dark"
              align="center"
              className="mb-10 sm:mb-14"
            />

            {/* ── Skeleton ── */}
            {loading && (
              <div className="space-y-4 mb-12">
                <div className="w-full rounded-2xl overflow-hidden bg-white/5 border border-white/10 animate-pulse" style={{ paddingBottom: '42%' }} />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 aspect-video animate-pulse" />
                  ))}
                </div>
              </div>
            )}

            {/* ── Videos ── */}
            {!loading && myData.length > 0 && (
              <div className="space-y-4 mb-12">

                {/* Featured video */}
                {featuredVideo && this.renderCard(featuredVideo, 0, true)}

                {/* Secondary videos */}
                {secondaryVideos.length > 0 && (
                  <div
                    className={`grid gap-4 ${
                      secondaryVideos.length === 1
                        ? 'grid-cols-1 sm:grid-cols-2'
                        : secondaryVideos.length === 2
                        ? 'grid-cols-1 sm:grid-cols-2'
                        : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                    }`}
                  >
                    {secondaryVideos.map((vid, i) => this.renderCard(vid, i + 1, false))}
                  </div>
                )}
              </div>
            )}

            {/* ── Empty state ── */}
            {!loading && myData.length === 0 && (
              <div className="text-center py-16 text-white/40">
                <FaVideo className="text-5xl mx-auto mb-4 opacity-30" />
                <p className="text-lg">No videos available yet.</p>
              </div>
            )}

            {/* ── CTA ── */}
            {!loading && myData.length > 0 && (
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Link
                  to="/allvideogallery"
                  className="inline-flex items-center gap-2.5 bg-secondary-600 hover:bg-secondary-500 text-white font-semibold py-3.5 px-9 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                  style={{ textDecoration: 'none' }}
                >
                  View All Videos
                  <FaArrowRight className="text-sm" />
                </Link>
              </motion.div>
            )}
          </div>
        </section>

        {/* ── Modal ── */}
        <AnimatePresence>
          {isModalOpen && selectedVideo && (
            <motion.div
              className="fixed inset-0 z-[999] flex items-center justify-center"
              style={{ background: 'rgba(0,0,0,0.96)', backdropFilter: 'blur(18px)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={this.closeVideoModal}
            >
              <motion.div
                className="relative w-full mx-3 sm:mx-6 max-w-4xl"
                initial={{ scale: 0.94, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.94, opacity: 0, y: 20 }}
                transition={{ duration: 0.28, type: 'spring', damping: 24 }}
                onClick={e => e.stopPropagation()}
              >
                {/* ── Top bar: close + title ── */}
                <div className="flex items-center gap-3 mb-3">
                  <motion.button
                    className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white text-sm hover:bg-white/20 active:scale-90 transition-all duration-150"
                    onClick={this.closeVideoModal}
                    whileTap={{ scale: 0.88 }}
                  >
                    <FaTimes />
                  </motion.button>
                  <div className="flex items-center gap-2 min-w-0">
                    <FaYoutube className="text-red-500 text-base sm:text-lg flex-shrink-0" />
                    <p className="text-white/80 text-xs sm:text-sm font-medium truncate leading-tight">
                      {selectedVideo.video_title}
                    </p>
                  </div>
                </div>

                {/* ── Video frame ── */}
                <div
                  className="relative w-full rounded-xl sm:rounded-2xl overflow-hidden bg-black border border-white/10"
                  style={{ boxShadow: '0 24px 64px rgba(0,0,0,0.8)' }}
                >
                  {/* 16:9 intrinsic ratio container */}
                  <div className="w-full" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      src={this.getYouTubeEmbedUrl(selectedVideo.video_link)}
                      title={selectedVideo.video_title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full border-0"
                    />
                  </div>
                </div>

                {/* ── Hint ── */}
                <p className="text-center mt-3 text-white/25 text-[11px]">
                  Tap outside to close
                </p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }
}

export default VideoGallery;
